import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, arrayUnion, addDoc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../../firebaseConfig';
import Calendar from '../../../components/Calendar';
import ConfirmationModal from '../../../components/ConfirmationModal';
import './index.css';

const MyVillas = ({ setBookings }) => {
  const [villas, setVillas] = useState([]);
  const [selectedVilla, setSelectedVilla] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successBooking, setSuccessBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ownerDetails, setOwnerDetails] = useState({ ownerId: null, ownerEmail: null, ownerName: null });

  const auth = getAuth();

  useEffect(() => {
    const fetchOwnerDetails = async () => {
      const user = auth.currentUser;
      if (!user) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        const profileDocRef = doc(db, 'users', user.uid, 'ownerDetails', 'profile');
        const profileDoc = await getDoc(profileDocRef);
        if (profileDoc.exists()) {
          const userData = profileDoc.data();
          setOwnerDetails({
            ownerId: user.uid,
            ownerEmail: userData.email || user.email,
            ownerName: userData.name || 'Unknown User',
            phoneNumber: userData.phone || '-',
          });
        } else {
          setError('Profile data not found in Firestore');
        }
      } catch (err) {
        setError('Failed to fetch user details');
        console.error('Error fetching user details:', err);
      }
    };

    const fetchVillas = async () => {
      try {
        const villasCollection = collection(db, 'villas');
        const villaSnapshot = await getDocs(villasCollection);
        const villaList = villaSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setVillas(villaList);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch villas');
        setLoading(false);
      }
    };

    fetchOwnerDetails();
    fetchVillas();
  }, [auth.currentUser]);

  const sendBookingEmail = async (booking) => {
    try {
      const response = await fetch('https://luxe-backend.netlify.app/send-owner-booking-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userType: 'owner',
          ownerEmail: ownerDetails.ownerEmail,
          ownerName: ownerDetails.ownerName,
          bookingId: booking.id,
          villa: booking.villaName,
          checkIn: booking.startDate,
          checkOut: booking.endDate,
          total: booking.basePricePerNight,
          phoneNumber: ownerDetails.phoneNumber || '-',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send booking email');
      }
    } catch (error) {
      console.error('Error sending booking email:', error);
    }
  };

  const handleBooking = async () => {
    console.log('MyVillas: handleBooking called with:', { selectedVilla, selectedDates, ownerDetails });
    if (typeof setBookings !== 'function') {
      console.error('setBookings is not a function');
      setError('Invalid booking function');
      return;
    }

    if (!ownerDetails.ownerId) {
      console.error('User not authenticated');
      setError('User not authenticated');
      return;
    }

    if (!selectedVilla || !selectedDates || selectedDates.length !== 2) {
      console.error('Invalid booking data:', { selectedVilla, selectedDates });
      setError('Invalid booking data');
      return;
    }

    const booking = {
      villaId: selectedVilla.id,
      villaName: selectedVilla.title,
      startDate: selectedDates[0].toISOString().split('T')[0],
      endDate: selectedDates[1].toISOString().split('T')[0],
      ownerId: ownerDetails.ownerId,
      basePricePerNight: selectedVilla.basePricePerNight,
      additionalGuestCost: selectedVilla.additionalGuestCost,
      securityDeposit: selectedVilla.securityDeposit,
      status: 'active',
      timestamp: new Date(),
    };

    try {
      console.log('MyVillas: Adding booking to Firestore:', booking);
      const bookingsCollection = collection(db, 'bookings');
      const bookingDoc = await addDoc(bookingsCollection, booking);
      const villaRef = doc(db, 'villas', selectedVilla.id);
      const dateRange = [];
      let currentDate = new Date(selectedDates[0]);
      const endDate = new Date(selectedDates[1]);
      while (currentDate <= endDate) {
        dateRange.push(currentDate.toISOString().split('T')[0]);
        currentDate.setDate(currentDate.getDate() + 1);
      }
      console.log('MyVillas: Blocking dates:', dateRange);
      await updateDoc(villaRef, {
        blockedDates: arrayUnion(...dateRange),
      });
      setBookings((prev) => [...prev, { ...booking, id: bookingDoc.id }]);
      setVillas((prev) =>
        prev.map((v) =>
          v.id === selectedVilla.id
            ? { ...v, blockedDates: [...(v.blockedDates || []), ...dateRange] }
            : v
        )
      );
      setSuccessBooking({
        title: selectedVilla.title,
        startDate: selectedDates[0],
        endDate: selectedDates[1],
      });

      await sendBookingEmail({ ...booking, id: bookingDoc.id });

      setShowConfirm(false);
      setShowSuccess(true);
      setSelectedVilla(null);
      setSelectedDates([]);
    } catch (err) {
      setError('Failed to create booking');
      console.error('MyVillas: Booking error:', err.message);
    }
  };

  const handleBookNow = (villa, dates) => {
    console.log('MyVillas: handleBookNow called with:', { villa, dates });
    setSelectedVilla(villa);
    setSelectedDates(dates);
    setShowConfirm(true);
  };

  if (loading) return <div className="spinner-container"><div className="spinner"></div></div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="container py-5">
      <h2 className="mb-4" style={{ fontFamily: "Josefin Sans" }}>My Villas</h2>
      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4">
        {villas.map((villa) => (
          <div key={villa.id} className="col">
            <div className="card-dash villa-card-dash">
              <img
                src={villa.imageUrls[0] || 'https://via.placeholder.com/300'}
                className="card-img-top"
                alt={villa.title}
              />
              <div className="card-body-dash">
                <h5 className="card-title mt-2">{villa.title}</h5>
                <p className="card-text">{villa.location}</p>
                <button
                  className="btn btn-primary w-100"
                  onClick={() => setSelectedVilla(villa)}
                >
                  Check Availability
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {selectedVilla && (
        <Calendar
          villa={selectedVilla}
          selectedDates={selectedDates}
          setSelectedDates={setSelectedDates}
          onClose={() => setSelectedVilla(null)}
          onBookNow={handleBookNow}
        />
      )}
      <ConfirmationModal
        show={showConfirm}
        onClose={() => {
          console.log('MyVillas: ConfirmationModal closed without confirming');
          setShowConfirm(false);
          setSelectedVilla(null);
          setSelectedDates([]);
        }}
        onConfirm={() => {
          console.log('MyVillas: ConfirmationModal confirmed');
          handleBooking();
        }}
        title="Confirm Booking"
        message={`Book ${selectedVilla?.title} from ${selectedDates[0]?.toLocaleDateString()} to ${selectedDates[1]?.toLocaleDateString()}? Base Price: ₹${selectedVilla?.basePricePerNight}/night, Security Deposit: ₹${selectedVilla?.securityDeposit}`}
      />
      <ConfirmationModal
        show={showSuccess}
        onClose={() => {
          console.log('MyVillas: SuccessModal closed');
          setShowSuccess(false);
        }}
        onConfirm={() => {
          console.log('MyVillas: SuccessModal confirmed');
          setShowSuccess(false);
        }}
        title="Booking Confirmed"
        message={`Your booking for ${successBooking?.title} from ${successBooking?.startDate?.toLocaleDateString()} to ${successBooking?.endDate?.toLocaleDateString()} has been confirmed! A confirmation email has been sent to ${ownerDetails.ownerEmail}.`}
      />
    </div>
  );
};

export default MyVillas;